
import { defineComponent } from 'vue'
import type { PropType } from 'vue'
import ActionIcons from './ActionIcons.vue'
import TheTag from '../Shared/TheTag.vue'
import BaseTable from '../Shared/BaseTable.vue'
import { createNamespacedHelpers } from 'vuex-composition-helpers'
import { useModalConfirm } from '@/utils/useModalDialogs'
import {
  TemplateInterface,
  KindType
} from '@/models/templates/Template.interface'

export default defineComponent({
  components: {
    ActionIcons,
    TheTag,
    BaseTable
  },
  props: {
    kind: { type: String as PropType<KindType>, required: true }
  },
  setup(props) {
    const { useActions, useGetters } = createNamespacedHelpers('templates')

    const { loadTemplates, copyTemplateVariants, destroyTemplateVariants } =
      useActions([
        'loadTemplates',
        'copyTemplateVariants',
        'destroyTemplateVariants'
      ])
    const { templatesGroupedBySlug } = useGetters(['templatesGroupedBySlug'])
    const { exportTemplate } = useActions(['exportTemplate'])
    const removeDuplicates = (array: string[]) => {
      return [...new Set(array)]
    }
    const getTemplatesLanguages = (templates: TemplateInterface[]) => {
      const languages: string[] = templates.map(
        (template) => template.language || ''
      )
      return removeDuplicates(languages).sort()
    }
    const getTemplatesTonalities = (templates: TemplateInterface[]) => {
      const tonalities: string[] = templates.map(
        (template) => template.tonality || ''
      )
      return removeDuplicates(tonalities).sort()
    }
    const getTemplatesGroups = (templates: TemplateInterface[]) => {
      const groups = templates.map(({ group }) => group || '').filter(Boolean)
      return removeDuplicates(groups).sort()
    }
    const getTemplatesTags = (templates: TemplateInterface[]) => {
      const tags = templates.map((template) => template.tags ?? []).flat()
      return removeDuplicates(tags).sort()
    }

    const getLatestUpdatedAtDate = (templates: TemplateInterface[]) => {
      return templates
        .map((template) => template.updatedAt)
        .sort()
        .slice(-1)[0]
    }

    const copyTemplateVariantsHandler = (slug: string) => {
      useModalConfirm(
        `Are you sure you want to duplicate the "${slug}" template?`,
        async () => {
          await copyTemplateVariants(slug)
          loadTemplates()
        },
        () => undefined
      )
    }

    const destroyTemplateVariantsHandler = (slug: string) => {
      useModalConfirm(
        `Are you sure you want to delete the "${slug}" template?`,
        () => destroyTemplateVariants({ slug, kind: props.kind }),
        () => undefined
      )
    }

    return {
      templatesGroupedBySlug,
      exportTemplate,
      getTemplatesLanguages,
      getTemplatesTonalities,
      getTemplatesGroups,
      getTemplatesTags,
      getLatestUpdatedAtDate,
      copyTemplateVariantsHandler,
      destroyTemplateVariantsHandler
    }
  }
})
